import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Disc3, Loader2, MessageCircle, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth-context";
import { fmtErr, CONSENT_VERSION, discordStart } from "../lib/api";

/**
 * Full Spectrum Center uses Discord as its verified account gateway. Email and
 * password entry is intentionally unavailable, so browser saved credentials
 * cannot become an unverified route into Center member tools.
 */
export default function Login() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const returnTo = search.get("return_to") || "/";
  const { user } = useAuth();
  const [error, setError] = useState("");
  const [discordBusy, setDiscordBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") navigate(returnTo, { replace: true });
  }, [user, returnTo, navigate]);

  const onDiscordSignIn = async () => {
    setError("");
    if (!accepted) {
      setError("Please accept the Terms and Privacy Policy to continue.");
      return;
    }
    setDiscordBusy(true);
    try {
      try { sessionStorage.setItem(`sr-consent-accepted-v${CONSENT_VERSION}`, "1"); } catch { /* browser storage is optional */ }
      const result = await discordStart(returnTo);
      if (!result.configured || !result.auth_url) {
        setError("Discord verification is not available right now. Please try again later.");
        return;
      }
      window.location.assign(result.auth_url);
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail, "Discord verification could not start. Please try again."));
    } finally {
      setDiscordBusy(false);
    }
  };

  return (
    <main className="min-h-screen sr-aurora grid place-items-center px-4 py-10">
      <section className="w-full max-w-md sr-glass rounded-2xl p-8 sr-fade-up" aria-labelledby="center-signin-title">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-gradient-to-br from-[#FF007F] to-[#00F0FF]"><Disc3 className="w-5 h-5 text-white" /></div>
          <div><div className="font-display text-lg font-bold">Full Spectrum Center</div><div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Verified member sign in</div></div>
        </div>

        <h1 id="center-signin-title" className="font-display text-3xl font-black tracking-tight mb-2">Enter the Center.</h1>
        <p className="text-sm text-white/55 mb-6">Full Spectrum Center uses Discord verification for every member account. Your public Center username remains separate from your Discord name.</p>

        <div className="mb-5 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] px-4 py-3 flex gap-3 text-sm text-[#C7FBFF]"><ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" /><p>Email and password sign in is not available. Discord verifies your account before Center access is created.</p></div>

        <label className="flex items-start gap-3 mb-4 cursor-pointer select-none" data-testid="login-consent-row">
          <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FF007F] cursor-pointer" data-testid="login-consent-checkbox" />
          <span className="text-xs text-white/70 leading-snug">I am 18+ and I accept Full Spectrum Center's <Link to="/legal/terms" target="_blank" className="text-[#00F0FF] underline hover:opacity-80" data-testid="login-terms-link">Terms of Service</Link> and <Link to="/legal/privacy" target="_blank" className="text-[#00F0FF] underline hover:opacity-80" data-testid="login-privacy-link">Privacy Policy</Link>.</span>
        </label>

        {error && <p className="mb-4 text-sm text-[#FF007F]" data-testid="login-error">{error}</p>}
        <button type="button" onClick={onDiscordSignIn} disabled={discordBusy || !accepted} className="w-full rounded-lg border border-[#5865F2]/60 bg-[#5865F2]/15 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#5865F2]/30 disabled:cursor-not-allowed disabled:opacity-40 flex items-center justify-center gap-2" data-testid="login-discord-btn">
          {discordBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />} Continue with Discord
        </button>
        <p className="mt-3 text-center text-xs text-white/45">No Discord account? <a href="https://discord.com/register" target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">Create one</a> and return here. <a href="https://support.discord.com/hc/en-us" target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">Trouble with Discord?</a></p>
        <p className="mt-6 text-center text-xs text-white/40">First time here? Continuing with Discord creates your verified Center account, then lets you choose your public Center username.</p>
      </section>
    </main>
  );
}
